import React, { useContext } from 'react';
import { AttributeValueSizeType } from '../../Components/Attributes/AttributeValue/AttributeValue';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../Typography/EDTypography';
import { GridContext } from '../../Features/GridContext';
import { VisningsTypContext } from '../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/VisningsTypContext/VisningsTypContext';

interface IProps {
  bold?: boolean;
  disabled?: boolean;
  fillContainer?: boolean;
  flexboxCenterChildren?: boolean;
  header?: boolean;
  hideLabel?: boolean;
  label: string;
  name: string;
  noLeftMargin?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  smallText?: boolean;
  size?: AttributeValueSizeType;
  testId?: string;
  topMargin?: boolean;
  valueOnNewRow?: boolean;
}

const useStyles = makeStyles<IProps, 'container'>()(
  (theme: Theme, props, classes) =>
    ({
      container: {
        boxSizing: 'border-box',
        padding: theme.spacing(
          props.topMargin === true
            ? 1
            : props.topMargin === false || props.header
            ? 0
            : 1,
          1,
          props.header ? 1 : 0,
          props.noLeftMargin ? 0 : 1
        ),
        lineHeight: 1.2,
        width: '50%',
        flex: '0 1 auto',
        '& p': {
          display: 'inline-block',
          lineHeight: 1.2,
          margin: theme.spacing(0),
          padding: theme.spacing(0),
          verticalAlign: 'top',
        },
        '& label': {
          fontWeight:
            props.bold == undefined || props.bold === true ? 'bold' : '',
        },
        [theme.breakpoints.only('xs')]: {
          width: '100%',
        },
      },
      containerSmall: {
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          width: '50%',
        },
        [theme.breakpoints.up('md')]: {
          width: '33%',
        },
      },
      containerMedium: {
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          width: '50%',
        },
        [theme.breakpoints.up('md')]: {
          width: '50%',
        },
      },
      containerLarge: {
        width: '100%',
      },
      griddedContainer: {
        // FIXME: varför inte grid gap?
        boxSizing: 'border-box',
        padding: props.header
          ? 0
          : theme.breakpoints.only('xs')
          ? 0
          : theme.spacing(1, 0, 0),
        lineHeight: 1.25,
        width: '100%',
        '& p': {
          display: 'block',
          lineHeight: 1.2,
          margin: theme.spacing(0),
          padding: theme.spacing(0),
          verticalAlign: 'top',
          [theme.breakpoints.only('xs')]: {
            display: 'inline-block',
          },
        },
        '&:empty': {
          display: 'none',
        },
      },
      detailsRightGridded: {
        '& p': {
          display: 'inline-block !important',
        },
      },
      fillContainer: {
        padding: theme.spacing(0),
      },
      containerView: {
        width: '100%',
      },
      label: {
        marginRight: theme.spacing(0.5),
        lineHeight: 1.2,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
      valueOnNewRow: {
        [`.${classes.container}>&`]: {
          display: 'block',
        },
      },
      flexboxCenterChildren: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: 0,
      },
    } as const)
);

/**
 *  Rubrik och värde (Caption & Value), visar ett värde med en rubrik för att beskriva vad värdet är för något. Kan användas i listor och kort.
 */
export const EDCaptionedValue: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes, cx } = useStyles(props);
  const gridContext = useContext(GridContext);
  const visningsTypContext = useContext(VisningsTypContext);
  const label = props.header ? props.label.toUpperCase() : props.label;
  const bold = props.bold == undefined || props.bold == true;

  const getSizeClassName = (
    size: AttributeValueSizeType | undefined
  ): string => {
    let result = '';
    switch (size) {
      case 'small':
        result = classes.containerSmall;
        break;

      case 'medium':
        result = classes.containerMedium;
        break;

      case 'large':
        result = classes.containerLarge;
        break;

      case 'ownblock':
        result = classes.containerLarge;
        break;
      default:
        break;
    }

    return result;
  };

  return (
    <div
      className={cx(classes.container, getSizeClassName(props.size), {
        [classes.containerView]: props.header,
        [classes.fillContainer]: props.fillContainer,
        [classes.containerLarge]: props.fillContainer,
        [classes.detailsRightGridded]: visningsTypContext.showDetailsRight,
        [classes.griddedContainer]: gridContext.inGrid,
        [classes.flexboxCenterChildren]: props.flexboxCenterChildren,
      })}
      onClick={props.onClick}
      key={props.name}
      data-testid={props.testId}
    >
      {!props.hideLabel && (
        <EDTypography
          variant={props.header || props.smallText ? 'caption' : 'body2'}
          className={cx({
            [classes.label]: !props.header,
            [classes.disabled]: props.disabled,
            [classes.valueOnNewRow]: props.valueOnNewRow,
          })}
          color={props.header ? 'textSecondary' : undefined}
          bold={bold}
        >
          <label htmlFor={props.name}>
            {`${label}${!props.header ? ':' : ''}`}
          </label>
          &nbsp;
        </EDTypography>
      )}
      {props.children}
    </div>
  );
};

export default EDCaptionedValue;
